import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTracker } from '@dpdgroupuk/react-event-tracker';

import { useOverlay } from '../../../components/Overlay';
import {
  CLEAR_SELECTED,
  CLOSE_TOTE,
  MERGE,
  MOVE_TO,
  OPTIMISE,
  RECONSOLIDATE,
  REMOVE,
  SAVE,
  SCHEDULE,
  SELECT_MULTIPLE,
  TRANSFER,
} from '../../../constants/strings';
import { startScheduleRoute } from '../../../store/actions/schedule';
import { saveRoute } from '../../../store/reducers/route';
import { clearSelectedResetOrder } from '../../../store/actions/ui/routeDetail';
import {
  getRouteDetail,
  isRouteReadyOrOpen,
  isRouteStatusSuitableForReConsolidation,
  isSaveRouteEnabled,
  isScheduleRequired,
} from '../../../store/selectors/route';
import { hasDepotAllowCloseTote } from '../../../store/selectors/app';
import {
  getMergeButtonDisableState,
  getMoveToButtonDisabledState,
  getRemoveParcelsDisabledState,
  getSelectedPreparedStops,
  getStopTransferDisabledState,
} from '../../../store/selectors/ui/routeDetail';
import useMergeStopsModalData from './useMergeStopsModalData';
import useMoveToModalData from './useMoveToModalData';
import useSelectMultipleModalData from './useSelectMultipleModalData';
import useTransferToModalData from './useTransferToModalData';
import useRemoveParcelsModalData from './useRemoveParcelsModalData';
import useCloseToteModalData from './useCloseToteModalData';
import { ROUTE_MANAGEMENT } from '../../../constants/analytics';
import { getSuitableStopsForReConsolidation } from '../../../models/route';
import useReconsolidationModalData from './useReconsolidationModalData';

export default () => {
  const dispatch = useDispatch();
  const tracker = useTracker();
  const { showOverlayWhile } = useOverlay();

  const selectedStops = useSelector(getSelectedPreparedStops);
  const isMoveToDisabled = useSelector(getMoveToButtonDisabledState);
  const isMergeDisabled = useSelector(getMergeButtonDisableState);
  const scheduleRequired = useSelector(isScheduleRequired);
  const saveRouteEnabled = useSelector(isSaveRouteEnabled);
  const isTransferDisabled = useSelector(getStopTransferDisabledState);
  const isRemoveDisabled = useSelector(getRemoveParcelsDisabledState);
  const routeReadyOrOpen = useSelector(isRouteReadyOrOpen);
  const closeToteEnable = useSelector(hasDepotAllowCloseTote);
  const isCurrentStatusSuitableForReConsolidation = useSelector(
    isRouteStatusSuitableForReConsolidation
  );
  const routeDetail = useSelector(getRouteDetail);

  const { openSelectMultipleStops } = useSelectMultipleModalData();
  const { openMoveToModal } = useMoveToModalData();
  const { openMergeStopsModal } = useMergeStopsModalData();
  const { openTransferToModal } = useTransferToModalData();
  const { openRemoveParcelsModal } = useRemoveParcelsModalData();
  const { openCloseToteModal } = useCloseToteModalData();
  const { openReconsolidationNoticeModal } = useReconsolidationModalData();

  const clearSelection = useCallback(() => {
    tracker.logEvent(ROUTE_MANAGEMENT.CLICK_CLEAR_SELECTED);
    dispatch(clearSelectedResetOrder());
  }, [dispatch, tracker]);

  const onOptimiseClick = useCallback(() => {
    tracker.logEvent(ROUTE_MANAGEMENT.CLICK_OPTIMISE);
    dispatch(startScheduleRoute(true));
  }, [dispatch, tracker]);

  const onScheduleClick = useCallback(() => {
    tracker.logEvent(ROUTE_MANAGEMENT.CLICK_SCHEDULE);
    dispatch(startScheduleRoute(false));
  }, [dispatch, tracker]);

  const onSaveClick = useCallback(() => {
    tracker.logEvent(ROUTE_MANAGEMENT.CLICK_SAVE);
    dispatch(saveRoute());
  }, [dispatch, tracker]);

  //TODO add action IDs
  const onTransferClick = useCallback(() => {
    openTransferToModal({ isStopsSelected: true });
  }, [openTransferToModal]);

  const availableForReConsolidation = useMemo(
    () =>
      (routeDetail && getSuitableStopsForReConsolidation(routeDetail?.stop)) ||
      {},
    [routeDetail]
  );

  const showReconsolidationNotice = useCallback(async () => {
    openReconsolidationNoticeModal(availableForReConsolidation);
  }, [openReconsolidationNoticeModal, availableForReConsolidation]);

  return useMemo(() => {
    const closeToteBtn = closeToteEnable
      ? [
          {
            title: CLOSE_TOTE,
            onClick: openCloseToteModal,
            isDisabled: false,
            highlighted: true,
          },
        ]
      : [];

    const reconsolidateBtn =
      isCurrentStatusSuitableForReConsolidation &&
      !!Object.keys(availableForReConsolidation).length
        ? [
            {
              title: RECONSOLIDATE,
              onClick: showReconsolidationNotice,
              isDisabled: false,
            },
          ]
        : [];

    return [
      {
        title: selectedStops.length ? CLEAR_SELECTED : SELECT_MULTIPLE,
        onClick: selectedStops.length
          ? showOverlayWhile(clearSelection)
          : openSelectMultipleStops,
        isDisabled: false,
      },
      {
        title: MOVE_TO,
        onClick: openMoveToModal,
        isDisabled: isMoveToDisabled,
      },
      {
        title: MERGE,
        onClick: openMergeStopsModal,
        isDisabled: isMergeDisabled,
      },
      {
        title: OPTIMISE,
        onClick: onOptimiseClick,
        isDisabled: false,
        isHidden: !routeReadyOrOpen,
      },
      {
        title: SCHEDULE,
        onClick: onScheduleClick,
        isDisabled: !scheduleRequired,
      },
      {
        title: SAVE,
        onClick: onSaveClick,
        isDisabled: !saveRouteEnabled,
      },
      {
        title: TRANSFER,
        onClick: onTransferClick,
        isDisabled: isTransferDisabled,
      },
      {
        title: REMOVE,
        onClick: openRemoveParcelsModal,
        isDisabled: isRemoveDisabled,
      },
      ...closeToteBtn,
      ...reconsolidateBtn,
    ];
  }, [
    clearSelection,
    openSelectMultipleStops,
    selectedStops,
    showOverlayWhile,
    openMoveToModal,
    isMoveToDisabled,
    isMergeDisabled,
    openMergeStopsModal,
    onOptimiseClick,
    onScheduleClick,
    scheduleRequired,
    onSaveClick,
    saveRouteEnabled,
    onTransferClick,
    isTransferDisabled,
    openRemoveParcelsModal,
    isRemoveDisabled,
    routeReadyOrOpen,
    openCloseToteModal,
    closeToteEnable,
    availableForReConsolidation,
    isCurrentStatusSuitableForReConsolidation,
    showReconsolidationNotice,
  ]);
};
